import { useState, useEffect } from 'react'
import { companyApi } from '../services/api'

/**
 * The company vehicle plates, fetched once and shared.
 *
 * The list used to be hardcoded in constants/plates.js, so adding a truck was a
 * code change and a deploy. It now comes from the database, edited on the
 * Company Information page — one list, shared by the timesheet and by fuel
 * expenses, which is the reason it never belonged to either form.
 *
 * The cache is module-level rather than per-component: a screen can mount
 * several pickers and they should not each fetch the same short list. It lives
 * as long as the page does, so editing the list calls refreshPlates().
 */
let cached = null
let inflight = null
const listeners = new Set()

function load() {
    if (!inflight) {
        inflight = companyApi.getPlates()
            .then(response => {
                cached = response?.success ? response.plates || [] : []
                return cached
            })
            .catch(() => {
                // The picker keeps its "Other — type it in" escape hatch, so a
                // failure here costs the convenience of the list, never the
                // ability to enter a plate.
                cached = []
                return cached
            })
    }
    return inflight
}

/** Drops the cache and re-reads the list, telling every mounted picker. */
export function refreshPlates() {
    cached = null
    inflight = null
    return load().then(plates => {
        listeners.forEach(notify => notify(plates))
        return plates
    })
}

/**
 * Returns { plates, loaded }. `loaded` matters: until the list has arrived,
 * nothing can tell an unknown plate from one that simply has not loaded yet,
 * and treating the second as the first makes the picker jump.
 */
export function usePlates() {
    const [plates, setPlates] = useState(cached || [])
    const [loaded, setLoaded] = useState(cached !== null)

    useEffect(() => {
        let alive = true
        listeners.add(setPlates)

        load().then(list => {
            if (!alive) return
            setPlates(list)
            setLoaded(true)
        })

        return () => {
            alive = false
            listeners.delete(setPlates)
        }
    }, [])

    return { plates, loaded }
}
